import { AoxMeasurementResult } from '../interfaces/biozoom-api/aox-measurement-result';
import { PreciseAoxMeasurementResult } from '../interfaces/biozoom-api/precise-aox-measurement-result';
import {StateInterface} from '../interfaces/biozoom-api/app-configuration';
import {License, MeasurementType} from '../interfaces/biozoom-api/license';
import {roundValueToTwoDigests} from './helpers';
import {Error} from '../interfaces/biozoom-api/aox-measurement-result';


export interface ErrorApp {
  raw: number;
  scaled: number;
  error: number;
}


/**
 * Calculate the amount of error criteria that went past a certain threshold for a single aox measurement
 * Note: Probably deprecated and to-be-overhauled
 * @param measurement singular AOX measurement
 */
export const getErrorCountAox = (measurement: AoxMeasurementResult) => {
  const tRepetition = 0.5;
  const tMotion = 0.5;
  const tBrightness = 0.5;
  const tPosition = 0.5;
  const tBrightnessIndividual = 0.5;
  const errors: string[] = [];
  if ((measurement.measurement_errors?.repetition_error ?? 0) >= tRepetition) {
    errors.push('repetition');
  }
  if (
    ((measurement.measurement_errors?.motion_error ?? 0) >= tMotion) ||
    ((measurement.measurement_errors?.individual_brightness_error ?? 0) >= tBrightnessIndividual)
  ) {
    errors.push('motion');
  }
  if ((measurement.measurement_errors?.individual_brightness_error ?? 0) >= tBrightness) {
    errors.push('brightness');
  }
  if ((measurement.measurement_errors?.positional_error_scaled ?? 0) >= tPosition) {
    errors.push('position');
  }
  if ((measurement.measurement_errors?.individual_brightness_error ?? 0) >= tBrightnessIndividual) {
    errors.push('brightness_individual');
  }
  return errors;
};

/**
 * Populate a single aox measurement's result object with proper error values
 *
 * This copies all base-errors into a dedicated error object, possibly after weighing in the sec. aox result
 * This error object will also contain additional errors
 * @param measurement singular AOX measurement
 */
export const processMeasurementAox = (measurement: AoxMeasurementResult) => {
  const fudgeFactor = 0.5;
  let motionErrorRaw: number;
  let motionErrorScaled: number;
  let motionErrorError: number;
  let motionError: ErrorApp;
  if (measurement.secondary_aox) {
    // TODO: How to factor in these errors? Average?
    // added rounded output of motion error
    motionErrorRaw = Math.max(0.1, (Math.abs(measurement.secondary_aox.aox - measurement.aox) / 2) ?? 0) * fudgeFactor;
    motionErrorScaled = roundValueToTwoDigests(motionErrorRaw / 0.15);
    motionErrorError = roundValueToTwoDigests((motionErrorRaw - 0.15) / 0.15);
    motionErrorRaw = roundValueToTwoDigests(motionErrorRaw);

    motionError = {
      raw: motionErrorRaw,
      scaled: motionErrorScaled,
      error: motionErrorError
    };

    measurement.aox = Math.floor((measurement.secondary_aox.aox + measurement.aox) / 2 * 10) / 10;
    measurement.aox_q = Math.round((measurement.secondary_aox.aox_q + measurement.aox_q) / 2 / 10) * 10;
  }
  measurement.measurement_errors = {
    motion_error: motionError,
    qnn_error: measurement?.error_prediction?.qnn_error,
    positional_error_scaled: measurement?.error_prediction?.positional_error_scaled,
    individual_brightness_error: measurement?.error_prediction?.individual_brightness_error,
    brightness_to_median_error: measurement?.error_prediction?.brightness_to_median_error,
    brightness_leakage: measurement?.error_prediction?.brightness_leakage,
    pressure_too_low: measurement?.error_prediction?.pressure_too_low,
    pressure_too_high: measurement?.error_prediction?.pressure_too_high,
    brightness_jitter: measurement?.error_prediction?.brightness_jitter,
  };
  // TODO: Return true only if at least one error is critical. Use 'getErrorCountAox'?
  return true;
};

/**
 * Populate each measurement's result object with the repetition error
 *
 * This error denotes how large the average error between this measurement and the other measurements
 * @param measurements singular AOX measurement
 */
export const processMeasurementsAox = (measurements: AoxMeasurementResult[]) => {
  if (measurements.length < 2) {
    return;
  }
  const fudgeFactor = 0.5;
  const lastMeasurementValue = measurements[measurements.length - 1].aox;
  let deviation: number;
  const deviationArray: number[] = [];
  let deviationMin: number;
  let aoxAvg: number;
  let deviationFromAvg: number;
  let aoxSum = lastMeasurementValue;
  let repetitionError: ErrorApp;


  for (let i = 0; i < measurements.length - 1; i++) {
    deviation = Math.abs(measurements[i].aox - lastMeasurementValue);
    deviationArray.push(deviation);
    aoxSum += measurements[i].aox;
  }

  deviationMin = Math.min(...deviationArray);
  aoxAvg = aoxSum / measurements.length;
  deviationFromAvg = Math.abs(lastMeasurementValue - aoxAvg);

  let repetitionErrorRaw = 0.25 * deviationFromAvg + 0.75 * deviationMin * fudgeFactor;
  const repetitionErrorScaled = roundValueToTwoDigests(repetitionErrorRaw / 0.15);
  const repetitionErrorError = roundValueToTwoDigests((repetitionErrorRaw - 0.15) / 0.15);
  repetitionErrorRaw = roundValueToTwoDigests(repetitionErrorRaw);

  repetitionError = {
    raw: repetitionErrorRaw,
    scaled: repetitionErrorScaled,
    error: repetitionErrorError
  };

  measurements[measurements.length - 1].measurement_errors.repetition_error = repetitionError;
};

//
//
//   for (let i = 0; i < measurements.length; i++) {
//     let sum = 0;
//     for (let j = 0; j < measurements.length; j++) {
//       if (i === j) {
//         continue;
//       }
//       sum += Math.abs(measurements[i].aox - measurements[j].aox);
//     }
//     sum = Math.round((sum / (measurements.length - 1)) * 100) / 100;
//     if (!measurements[i].measurement_errors) {
//       measurements[i].measurement_errors = {};
//     }
//     measurements[i].measurement_errors.repetition_error = sum;
//   }
// };

/**
 * Populate a precision measurement's result object with errors and additional values
 *
 * In case the veggie value is missing, it is re-calculated here.
 * @param measurement precision measurement
 * @param aoxData array of singular aox measurement results
 */
export const processMeasurementPreciseAox = (measurement: PreciseAoxMeasurementResult, aoxData: AoxMeasurementResult[]) => {
  for (const aox of aoxData) {
    if (!aox.measurement_errors) {
      aox.measurement_errors = {};
    }
    // aox.measurement_errors.repetition_error =
    //   Math.max(0.1, Math.round(Math.abs(aox.aox - Number(measurement.precise_aox ?? measurement.aox ?? aox.aox)) * 100) / 100);
  }

  if (!measurement.series_errors) {
    measurement.series_errors = [];
  }


  const veggieAvg = (aoxData.map((el) => el.veggie_01_avg ?? 0).reduce((a, b) => a + b)) / (aoxData?.length ?? 1);
  if (!measurement.veggie_01_avg && veggieAvg && veggieAvg > 0) {
    measurement.veggie_01_avg = Math.round(veggieAvg);
  }
  const intakeCategory = (aoxData.map((el) => el.intake_category ?? 0).reduce((a, b) => a + b)) / (aoxData?.length ?? 1);
  if (!measurement.intake_category && intakeCategory && intakeCategory > 0) {
    if (intakeCategory <= 150) {
      measurement.intake_category = 150;
    } else if (intakeCategory <= 225) {
      measurement.intake_category = 175;
    } else if (intakeCategory <= 275) {
      measurement.intake_category = 250;
    } else if (intakeCategory <= 350) {
      measurement.intake_category = 300;
    } else if (intakeCategory <= 400) {
      measurement.intake_category = 350;
    } else {
      measurement.intake_category = 500;
    }
  }
  while (measurement.series_errors.length < aoxData.length) {
    measurement.series_errors.push({});
  }
  aoxData.map((aox, i) => measurement.series_errors[i].repetition_error = aox?.measurement_errors?.repetition_error ?? null);
  aoxData.map((aox, i) => measurement.series_errors[i].motion_error = aox?.measurement_errors?.motion_error ?? null);

  let availableKeys = [];
  for (const seriesError of (measurement?.series_errors ?? [])) {
    for (const key of Object.keys(seriesError ?? {})) {
      availableKeys.push(key);
    }
  }
  availableKeys = availableKeys.filter((v, i, a) => a.indexOf(v) === i);
  const errorsSimple = {};
  for (const seriesError of (measurement?.series_errors ?? [])) {
    for (const key of availableKeys) {
      let err = seriesError[key] ?? 0;
      if (err && typeof err === 'object') {
        err = err.scaled ?? 0;
      }
      if (errorsSimple[key] && Array.isArray(errorsSimple[key])) {
        errorsSimple[key].push(err);
      } else {
        errorsSimple[key] = [err];
      }
    }
  }
  measurement.series_errors_simple = errorsSimple;
};

// NOTE: poor naming for the flatrates // aox == aoxQuick and aoxPrecise == aox =)
export const validateMeasurementAllowance = (
  measurementType: MeasurementType, state: StateInterface
) => {
  const license: License = state.LICENSE;
  if (!license || !license.valid) {
    return true;
  }
  const flatrate = license.scanner?.flatrate;
  switch (measurementType) {
    case 'aox':
      if (flatrate?.aoxQuick) {
        return true;
      }
      if (license.total.allowedMeasurements.aox < 1) {
        return false;
      }
      break;
    case 'aoxPrecise':
      if (flatrate?.aox) {
        return true;
      }
      if (license.total.allowedMeasurements.aoxPrecise < 1) {
        return false;
      }
      break;
    case 'hrv':
      if (flatrate?.hrv) {
        return true;
      }
      if (license.total.allowedMeasurements.hrv < 1) {
        return false;
      }
      break;
    case 'vitality':
      if (flatrate?.vitality) {
        return true;
      }
      if (license.total.allowedMeasurements.vitality < 1) {
        return false;
      }
      break;
    default:
      return true;
  }
  return true;
};
